(function () {
  const STORAGE_KEY = 'music-player-state';
  const SCRATCH_SENSITIVITY = 0.3;
  const DRAG_THRESHOLD = 25;

  const initMusicPlayer = () => {
    const player = document.querySelector('.music-player');
    if (!player) return;

    const btn = player.querySelector('.music-player__btn');
    const disc = player.querySelector('.music-player__disc');
    const audio = player.querySelector('.music-player__audio');
    if (!btn || !disc || !audio) return;

    const scratchEnabled = player.dataset.scratch === 'true';
    let saveThrottleId = null;

    const saveState = (force) => {
      if (!force && saveThrottleId) return;
      clearTimeout(saveThrottleId);
      saveThrottleId = setTimeout(() => {
        saveThrottleId = null;
      }, 1000);

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          currentTime: audio.currentTime,
          isPlaying: !audio.paused
        }));
      } catch (e) { /* storage full or unavailable */ }
    };

    const clearState = () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) { /* unavailable */ }
    };

    const setPlaying = (playing) => {
      player.classList.toggle('is-playing', playing);
    };

    const playAudio = () => {
      return audio.play().then(() => {
        setPlaying(true);
        saveState();
      }).catch(() => {
        setPlaying(false);
      });
    };

    const togglePlay = () => {
      if (audio.paused) {
        playAudio();
      } else {
        audio.pause();
        setPlaying(false);
        saveState(true);
      }
    };

    let touchedBtn = false;

    btn.addEventListener('touchend', (e) => {
      e.preventDefault();
      touchedBtn = true;
      togglePlay();
    });

    btn.addEventListener('click', () => {
      if (touchedBtn) {
        touchedBtn = false;
        return;
      }
      togglePlay();
    });

    audio.addEventListener('timeupdate', () => {
      if (!audio.paused) saveState();
    });

    audio.addEventListener('ended', () => {
      setPlaying(false);
      clearState();
    });

    audio.addEventListener('pause', () => {
      setPlaying(false);
    });

    audio.addEventListener('play', () => {
      setPlaying(true);
    });

    /* ── Disc click & scratch ────────────────────────── */

    const setPreservesPitch = (value) => {
      audio.preservesPitch = value;
      if ('webkitPreservesPitch' in audio) {
        audio.webkitPreservesPitch = value;
      }
    };

    if (scratchEnabled) {
      const MIN_RATE = 0.0625;
      const MAX_RATE = 4;

      let isScratching = false;
      let dragMoved = false;
      let wasPlaying = false;
      let lastAngle = 0;
      let lastMoveTime = 0;
      let cumulativeRotation = 0;
      let startX = 0;
      let startY = 0;
      let idleDecayId = null;
      const discImg = disc.querySelector('img');

      const getAngle = (clientX, clientY) => {
        const rect = disc.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        return Math.atan2(clientY - cy, clientX - cx);
      };

      const onScratchStart = (clientX, clientY) => {
        isScratching = true;
        dragMoved = false;
        startX = clientX;
        startY = clientY;
        wasPlaying = !audio.paused;
        lastAngle = getAngle(clientX, clientY);
        lastMoveTime = performance.now();
        cumulativeRotation = 0;

        setPreservesPitch(false);
        audio.playbackRate = MIN_RATE;

        if (audio.paused) {
          audio.play().catch(() => {});
        }

        player.classList.add('is-scratching');
      };

      const onScratchMove = (clientX, clientY) => {
        if (!isScratching) return;

        const dx = clientX - startX;
        const dy = clientY - startY;
        if (!dragMoved && (dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        dragMoved = true;

        const now = performance.now();
        const dt = (now - lastMoveTime) / 1000;
        lastMoveTime = now;

        const angle = getAngle(clientX, clientY);
        let delta = angle - lastAngle;

        if (delta > Math.PI) delta -= 2 * Math.PI;
        if (delta < -Math.PI) delta += 2 * Math.PI;

        cumulativeRotation += delta;
        lastAngle = angle;

        const timeDelta = delta * SCRATCH_SENSITIVITY;
        let newTime = audio.currentTime + timeDelta;
        newTime = Math.max(0, Math.min(audio.duration || 0, newTime));
        audio.currentTime = newTime;

        var speed = dt > 0
          ? Math.abs(delta / dt) * SCRATCH_SENSITIVITY
          : MIN_RATE;
        audio.playbackRate = Math.max(MIN_RATE, Math.min(MAX_RATE, speed));

        if (discImg) {
          discImg.style.transform = 'rotate(' + cumulativeRotation + 'rad)';
        }

        clearTimeout(idleDecayId);
        idleDecayId = setTimeout(() => {
          if (isScratching) audio.playbackRate = MIN_RATE;
        }, 60);
      };

      const onScratchEnd = () => {
        if (!isScratching) return;
        isScratching = false;
        clearTimeout(idleDecayId);

        audio.playbackRate = 1;
        setPreservesPitch(true);
        player.classList.remove('is-scratching');

        if (discImg) {
          discImg.style.transform = '';
        }

        if (!dragMoved) {
          if (wasPlaying) {
            audio.currentTime = 0;
          }
          playAudio();
          return;
        }

        if (wasPlaying) {
          setPlaying(true);
          saveState();
        } else {
          audio.pause();
          setPlaying(false);
          saveState();
        }
      };

      disc.addEventListener('mousedown', (e) => {
        e.preventDefault();
        onScratchStart(e.clientX, e.clientY);
      });

      document.addEventListener('mousemove', (e) => {
        if (!isScratching) return;
        onScratchMove(e.clientX, e.clientY);
      });

      document.addEventListener('mouseup', () => {
        onScratchEnd();
      });

      disc.addEventListener('touchstart', (e) => {
        e.preventDefault();
        var touch = e.touches[0];
        onScratchStart(touch.clientX, touch.clientY);
      });

      document.addEventListener('touchmove', (e) => {
        if (!isScratching) return;
        var touch = e.touches[0];
        onScratchMove(touch.clientX, touch.clientY);
      }, { passive: true });

      document.addEventListener('touchend', () => {
        onScratchEnd();
      });
    } else {
      disc.addEventListener('click', () => {
        if (audio.paused) {
          playAudio();
        } else {
          audio.currentTime = 0;
          saveState();
        }
      });
    }

    /* ── Restore session state ───────────────────────── */

    const restoreState = () => {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;

        const state = JSON.parse(raw);
        if (typeof state.currentTime === 'number' && state.currentTime > 0) {
          audio.currentTime = state.currentTime;
        }

        if (state.isPlaying) {
          playAudio();
        }
      } catch (e) { /* invalid or unavailable */ }
    };

    if (audio.readyState >= 1) {
      restoreState();
    } else {
      audio.addEventListener('loadedmetadata', restoreState, { once: true });
    }
  };

  initMusicPlayer();

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.music-player')) {
      initMusicPlayer();
    }
  });
})();
